<div class="kb-page" *ngIf="state$ | async as state">

  <!-- ============ TOP: Header + Upload ============ -->
  <div class="kb-top">
    <div class="kb-header">
      <h1 class="kb-title">Knowledge Base</h1>
      <span class="kb-project-badge">{{ projectId }}</span>
    </div>

    <app-project-documents
      [persistedFiles]="state.persistedFiles"
      [isLoading]="state.isLoadingFiles"
      [error]="state.errorFiles"
      [activeFilterFileId]="state.activeFileFilter?.id || null"
      [currentUserEmail]="currentUserEmail"
      [projectId]="projectId"
      [hideIndexed]="true"
      (fileSelectedForView)="handleFileSelectedForView($event)"
      (fileFilterChanged)="handleFileFilterChanged($event)"
      (fileUploaded)="handleFileUploaded($event)"
      (fileDeleted)="handleFileDeleted()"
      (indexAllRequested)="handleIndexAllRequest()">
    </app-project-documents>
  </div>

  <!-- ============ MIDDLE: Ask AI ============ -->
  <div class="kb-hero">

    <div *ngIf="!isChatOpen" class="kb-hero-inner">
      <div class="kb-search-box">
        <div class="kb-search-input-row">
          <textarea
            class="kb-search-input"
            placeholder="Ask anything..."
            [(ngModel)]="searchQueryControl"
            (keydown.enter)="$event.preventDefault(); openChatWithQuery()"
            rows="1"
            (input)="autoResize($event)"></textarea>
          <input type="number" class="kb-iterations-input"
                 [(ngModel)]="maxIterations"
                 min="1" max="20"
                 title="Max agent iterations">
          <button
            class="kb-btn-ask"
            (click)="openChatWithQuery()"
            [disabled]="!searchQueryControl?.trim()">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m22 2-7 20-4-9-9-4Z"/>
              <path d="M22 2 11 13"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div *ngIf="isChatOpen" class="kb-chat-overlay">
      <app-knowledge-base-chat
        [projectId]="projectId"
        [currentUserEmail]="currentUserEmail"
        [model]="selectedModel"
        [initialQuery]="searchQueryControl"
        [maxIterations]="maxIterations"
        (closeChat)="closeChat()"
        (openSource)="handleOpenSource($event)">
      </app-knowledge-base-chat>
    </div>
  </div>

  <!-- ============ BOTTOM: All Files ============ -->
  <div class="kb-files">
    <div class="kb-section-header">
      <div class="kb-section-title-row">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/>
        </svg>
        <h2 class="kb-section-title">Indexed Documents</h2>
        <span class="kb-count-badge" *ngIf="indexedFiles.length > 0">{{ indexedFiles.length }}</span>
      </div>
      <p class="kb-section-subtitle">These documents are searchable by the AI assistant</p>
    </div>

    <div class="kb-file-grid" *ngIf="indexedFiles.length > 0">
      <div class="kb-file-card" *ngFor="let file of indexedFiles"
           (click)="handleFileSelectedForView(file)">
        <div class="kb-file-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>
            <path d="M14 2v4a2 2 0 0 0 2 2h4"/>
          </svg>
        </div>
        <div class="kb-file-info">
          <span class="kb-file-name">{{ file.name }}</span>
          <span class="kb-file-meta">Indexed {{ file.timestamp | date:'mediumDate' }}</span>
        </div>
        <div class="kb-file-actions">
          <button class="kb-file-action-btn" title="Re-index"
                  (click)="handleIndexRequest(file); $event.stopPropagation()">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
          </button>
          <button class="kb-file-action-btn danger" title="Delete"
                  (click)="handleDeleteIndexedFile(file); $event.stopPropagation()">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18"/>
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
            </svg>
          </button>
          <svg class="kb-file-check" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </div>
      </div>
    </div>

    <div class="kb-empty-state" *ngIf="indexedFiles.length === 0">
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/>
      </svg>
      <p>No documents indexed yet.</p>
      <span>Upload files above to get started.</span>
    </div>
  </div>

  <!-- ============ KI from Ambiguity Resolutions ============ -->
  <div class="kb-ki-section" *ngIf="ambiguityKiItems.length > 0">
    <div class="kb-section-header">
      <div class="kb-section-title-row">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/>
        </svg>
        <h2 class="kb-section-title">KI from Ambiguity Resolutions</h2>
        <span class="kb-count-badge">{{ ambiguityKiItems.length }}</span>
      </div>
      <p class="kb-section-subtitle">These Knowledge Items were generated from ambiguity resolutions during analysis sessions.</p>
    </div>

    <div class="kb-ki-search">
      <input type="text" class="kb-ki-search-input" placeholder="Search KIs..."
             [(ngModel)]="kiSearchQuery" (input)="filterKiItems()" />
      <span class="kb-ki-search-count" *ngIf="kiSearchQuery.trim()">
        {{ filteredKiItems.length }} / {{ ambiguityKiItems.length }}
      </span>
    </div>

    <div class="kb-ki-table-wrapper">
      <table class="kb-ki-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Definition</th>
            <th>Source</th>
            <th>Last Modified</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr class="kb-ki-row" *ngFor="let ki of filteredKiItems | slice:0:kiDisplayLimit">
            <td class="kb-ki-title" (click)="openKiDetail(ki)">{{ ki.title }}</td>
            <td class="kb-ki-definition">{{ (ki.definition || ki.answer || '') | slice:0:80 }}{{ (ki.definition || ki.answer || '').length > 80 ? '...' : '' }}</td>
            <td><span class="badge-source">ambiguity_resolution</span></td>
            <td class="kb-ki-date">{{ ki.lastModified | date:'dd/MM/yyyy, HH:mm:ss' }}</td>
            <td class="kb-ki-actions">
              <button class="kb-ki-action-btn edit" title="Edit" (click)="openKiDetail(ki, true)">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                </svg>
              </button>
              <button class="kb-ki-action-btn danger" title="Delete" (click)="deleteKi(ki, $event)">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="kb-ki-show-more" *ngIf="filteredKiItems.length > kiDisplayLimit">
      <button class="btn btn-sm kb-btn-show-more" (click)="kiDisplayLimit = filteredKiItems.length">
        Show all ({{ filteredKiItems.length }})
      </button>
    </div>
  </div>

</div>

<!-- ============ KI Detail Modal ============ -->
<ng-template #kiDetailModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">KI Detail</h5>
    <button type="button" class="btn-close" (click)="isEditingKi ? cancelEditKi() : modal.dismiss()"></button>
  </div>
  <div class="modal-body" *ngIf="selectedKi">

    <div class="ki-detail-field">
      <label>Title</label>
      <input *ngIf="isEditingKi" class="form-control" [(ngModel)]="editKiData.title" />
      <p *ngIf="!isEditingKi">{{ selectedKi.title }}</p>
    </div>

    <div class="ki-detail-field">
      <label>Question (Clarification Needed) <span class="ki-embedding-tag">embedding</span></label>
      <textarea *ngIf="isEditingKi" class="form-control" rows="3" [(ngModel)]="editKiData.question"></textarea>
      <p *ngIf="!isEditingKi">{{ selectedKi.question || '—' }}</p>
    </div>

    <div class="ki-detail-field">
      <label>Impact</label>
      <textarea *ngIf="isEditingKi" class="form-control" rows="2" [(ngModel)]="editKiData.impact_text"></textarea>
      <p *ngIf="!isEditingKi">{{ selectedKi.impact_text || '—' }}</p>
    </div>

    <div class="ki-detail-field">
      <label>Answer <span class="ki-embedding-tag">embedding</span></label>
      <textarea *ngIf="isEditingKi" class="form-control" rows="4" [(ngModel)]="editKiData.answer"></textarea>
      <p *ngIf="!isEditingKi">{{ selectedKi.answer || selectedKi.definition || '—' }}</p>
    </div>

    <p class="ki-embedding-hint">
      Fields marked <span class="ki-embedding-tag">embedding</span> are combined into a single vector for semantic search in the KB.
    </p>

    <div class="ki-detail-story" *ngIf="selectedKiStory">
      <label>Reference Story (read only)</label>
      <div class="ki-story-content">{{ selectedKiStory }}</div>
    </div>

    <div class="ki-detail-meta">
      <span class="badge-source">{{ selectedKi.source_type }}</span>
      <span class="ki-meta-date">Last Modified: {{ selectedKi.lastModified | date:'dd/MM/yyyy, HH:mm:ss' }}</span>
    </div>
  </div>
  <div class="modal-footer">
    <!-- View mode -->
    <ng-container *ngIf="!isEditingKi">
      <button class="btn btn-outline-secondary btn-sm" (click)="modal.dismiss()">Close</button>
      <button class="btn btn-outline-danger btn-sm" (click)="deleteKi(selectedKi!)">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
        </svg> Delete
      </button>
      <button class="btn btn-sm kb-btn-edit" (click)="startEditKi()">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
        </svg> Edit
      </button>
    </ng-container>
    <!-- Edit mode -->
    <ng-container *ngIf="isEditingKi">
      <button class="btn btn-outline-secondary btn-sm" (click)="cancelEditKi()" [disabled]="isSavingKi">Cancel</button>
      <button class="btn btn-sm kb-btn-save" (click)="saveKi()" [disabled]="isSavingKi">
        <span *ngIf="!isSavingKi">Save</span>
        <span *ngIf="isSavingKi" class="ki-saving-indicator">
          <span class="ki-spinner"></span> Saving...
        </span>
      </button>
    </ng-container>
  </div>
</ng-template>
